import React, { useEffect, useState } from 'react'
import { apiGet } from '../../utils/api'
import { countryToFlag } from '../../utils/countryFlag'

interface AccountInfo {
  paid_customer_id: number
  wequote_id: number | null
  alias: string | null
  company_name: string | null
  email: string | null
  contact_name: string | null
  account_type: string | null
  status: string | null
  company_logo_url: string | null
  country: string | null
}
interface AccountResponse {
  wequote_account_id: string | null
  plan_tier: string | null
  match_method: string | null
  person: { email: string | null; name: string | null; phone?: string | null }
  account: AccountInfo | null
  visitor: {
    browser: string | null; os: string | null; device_type: string | null
    city: string | null; region: string | null; country: string | null
    source_ip: string | null; landing_page: string | null; current_page: string | null
  }
}
interface Metrics {
  initiated_at: string; status: string; department?: string
  first_agent?: string; last_agent?: string; first_response_at?: string
  queue_in_at?: string; queue_out_at?: string; missed_at?: string; missed_reason?: string
  duration_seconds?: number
}
interface ChatRow { id: number | string; status: string; created_at: string; visitor_email: string | null; visitor_name: string | null; resolution?: string | null; resolved?: boolean }
interface Analytics {
  company_name: string | null; alias: string | null
  this_user: { email: string; name: string | null } | null
  support_seconds_company: number; support_seconds_user: number; support_seconds_chat: number
  total_chats: number; resolved_count: number
  people: { email: string; name: string | null; chats: number }[]
  all_chats: ChatRow[]; unresolved_chats: ChatRow[]
  top_issues: { issue: string; count: number }[]
}
interface Props {
  chatId: string
  chat: { customerName?: string; status?: string; createdAt?: string; messages?: unknown[] }
}

function initials(name?: string | null): string {
  if (!name) return '?'
  return name.trim().split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('') || '?'
}
function fmtDateTime(s?: string): string {
  if (!s) return '—'
  const d = new Date(s)
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}
function fmtDur(seconds?: number): string {
  if (!seconds || seconds <= 0) return '—'
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
  const h = Math.floor(seconds / 3600); const m = Math.floor((seconds % 3600) / 60)
  return m ? `${h}h ${m}m` : `${h}h`
}
function timeDiff(a?: string, b?: string): string {
  if (!a || !b) return '—'
  return fmtDur(Math.abs(Math.floor((new Date(b).getTime() - new Date(a).getTime()) / 1000)))
}

// Render a value, or a muted "Unavailable" when it's missing/empty.
const un = (x?: React.ReactNode): React.ReactNode =>
  (x === 0 || (x !== '' && x !== null && x !== undefined && x !== false))
    ? x
    : <span className="text-txt-tertiary italic">Unavailable</span>

const InfoRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div className="flex items-start justify-between gap-3 py-1.5">
    <span className="text-xs text-txt-secondary shrink-0">{label}</span>
    <span className="text-xs text-txt-primary text-right break-words max-w-[170px]">{un(value)}</span>
  </div>
)
const Collapsible: React.FC<{ title: string; defaultOpen?: boolean; children: React.ReactNode }> = ({ title, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="border-b border-border">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between px-5 py-3 hover:bg-surface-hover transition-colors">
        <span className="text-[11px] font-semibold text-txt-secondary uppercase tracking-wider">{title}</span>
        <svg className={`w-4 h-4 text-txt-tertiary transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
      </button>
      {open && <div className="px-5 pb-4">{children}</div>}
    </div>
  )
}
const Stat: React.FC<{ label: string; value: string; hint?: string }> = ({ label, value, hint }) => (
  <div className="rounded-lg border border-border p-3">
    <p className="text-[10px] text-txt-secondary uppercase tracking-wide">{label}</p>
    <p className="text-base font-bold text-txt-primary mt-1">{value}</p>
    {hint && <p className="text-[10px] text-txt-tertiary truncate">{hint}</p>}
  </div>
)
const TABS = [
  { key: 'info' as const, label: 'Info', icon: <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /> },
  { key: 'analytics' as const, label: 'Analytics', icon: <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" /> },
]

export const WidgetAccountSidebar: React.FC<Props> = ({ chatId, chat }) => {
  const [tab, setTab] = useState<'info' | 'analytics'>('info')
  const [chatTab, setChatTab] = useState<'all' | 'unresolved'>('all')
  const [data, setData] = useState<AccountResponse | null>(null)
  const [metrics, setMetrics] = useState<Metrics | null>(null)
  const [analytics, setAnalytics] = useState<Analytics | null>(null)
  const [analyticsLoading, setAnalyticsLoading] = useState(false)

  useEffect(() => {
    let active = true
    apiGet<AccountResponse>(`/chats/${chatId}/account`).then(d => { if (active) setData(d) }).catch(() => {})
    apiGet<Metrics>(`/chats/${chatId}/metrics`).then(m => { if (active) setMetrics(m) }).catch(() => {})
    setAnalytics(null)
    return () => { active = false }
  }, [chatId])

  useEffect(() => {
    if (tab !== 'analytics' || analytics) return
    let active = true; setAnalyticsLoading(true)
    apiGet<Analytics>(`/chats/${chatId}/analytics`).then(a => { if (active) setAnalytics(a) }).catch(() => {}).finally(() => { if (active) setAnalyticsLoading(false) })
    return () => { active = false }
  }, [tab, chatId, analytics])

  const acc = data?.account || null
  const v = data?.visitor
  const person = data?.person
  const displayName = acc?.company_name || chat.customerName || 'Unknown Company'
  const idLabel = acc?.wequote_id ? `#${acc.wequote_id}` : (data?.wequote_account_id || '—')
  const location = [v?.city, v?.region, v?.country].filter(Boolean).join(', ')
  const openChat = (id: string | number) => window.open(`/queue/${id}`, '_blank', 'noopener')

  const chatList = chatTab === 'all' ? (analytics?.all_chats || []) : (analytics?.unresolved_chats || [])

  return (
    <div className="w-[300px] flex-shrink-0 min-h-0 h-full border-l border-border bg-surface flex flex-col">
      {/* Icon tabs — header stays put, never scrolls/clips */}
      <div className="flex border-b border-border flex-shrink-0">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex-1 flex flex-col items-center gap-1 py-2.5 transition-colors ${tab === t.key ? 'text-brand-600' : 'text-txt-secondary hover:text-txt-primary'}`}>
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}>{t.icon}</svg>
            <span className="text-[11px] font-medium">{t.label}</span>
            <span className={`block h-0.5 w-8 rounded-full mt-0.5 ${tab === t.key ? 'bg-brand-600' : 'bg-transparent'}`} />
          </button>
        ))}
      </div>

      {/* Scrollable body — absorbs tab content height so opening notes/analytics
          can't push the tabs header off-screen or clip the sidebar. */}
      <div className="flex-1 min-h-0 overflow-y-auto">
      {tab === 'info' && (
        <div>
          <div className="flex flex-col items-center text-center px-5 pt-6 pb-5 border-b border-border">
            {acc?.company_logo_url ? (
              <img src={acc.company_logo_url} alt="" className="w-14 h-14 rounded-full object-cover mb-3" />
            ) : (
              <div className="w-14 h-14 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center text-base font-bold mb-3">{initials(displayName)}</div>
            )}
            <p className="text-sm font-semibold text-txt-primary">
              {acc?.country && countryToFlag(acc.country) ? <span className="mr-1" title={acc.country}>{countryToFlag(acc.country)}</span> : null}
              {displayName}
            </p>
            {(person?.name || person?.email) && (
              <p className="text-xs text-txt-secondary mt-0.5">{person?.name || ''}{person?.email ? ` · ${person.email}` : ''}</p>
            )}
            {acc?.email && <a href={`mailto:${acc.email}`} className="text-xs text-brand-600 hover:underline mt-0.5 break-all">{acc.email}</a>}
            <div className="flex items-center gap-1.5 mt-2 flex-wrap justify-center">
              <span className="text-[10px] font-medium px-1.5 py-px rounded bg-slate-100 text-slate-600">ID: {idLabel}</span>
              {acc?.alias && <span className="text-[10px] font-medium px-1.5 py-px rounded bg-amber-100 text-amber-700">{acc.alias}</span>}
            </div>
            {!acc && <p className="text-[10px] text-txt-tertiary mt-2 max-w-[200px]">Unknown company — the widget passed no URL alias or account id to match against.</p>}
          </div>

          <Collapsible title="Visitor Details" defaultOpen>
            <InfoRow label="Name" value={person?.name} />
            <InfoRow label="Email" value={person?.email ? <a href={`mailto:${person.email}`} className="text-brand-600 hover:underline break-all">{person.email}</a> : undefined} />
            <InfoRow label="Mobile" value={person?.phone ? (
              <a href={`tel:${String(person.phone).replace(/[^\d+]/g, '')}`} className="inline-flex items-center gap-1 text-brand-600 hover:underline" title="Call this visitor">
                <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" /></svg>
                {person.phone}
              </a>
            ) : undefined} />
            <InfoRow label="Company" value={acc?.company_name} />
            <InfoRow label="Account ID" value={acc?.wequote_id ? `#${acc.wequote_id}` : undefined} />
            <InfoRow label="Browser" value={v?.browser} />
            <InfoRow label="OS" value={v?.os} />
            <InfoRow label="Device" value={v?.device_type ? <span className="capitalize">{v.device_type}</span> : undefined} />
            <InfoRow label="IP" value={v?.source_ip} />
            <InfoRow label="Location" value={location} />
            <InfoRow label="Current Page" value={v?.current_page ? <a href={v.current_page} target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline break-all">{v.current_page.replace(/^https?:\/\//, '')}</a> : undefined} />
            <InfoRow label="Landing Page" value={v?.landing_page ? <a href={v.landing_page} target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline break-all">{v.landing_page.replace(/^https?:\/\//, '')}</a> : undefined} />
          </Collapsible>

          <Collapsible title="Conversation Info" defaultOpen>
            <InfoRow label="Chat #" value={chatId} />
            <InfoRow label="Initiated At" value={fmtDateTime(metrics?.initiated_at || chat.createdAt)} />
            <InfoRow label="Status" value={<span className="capitalize">{metrics?.status || chat.status || '—'}</span>} />
            <InfoRow label="Messages" value={Array.isArray(chat.messages) ? chat.messages.length : '—'} />
            <InfoRow label="Department" value={metrics?.department || 'Unassigned'} />
            <InfoRow label="First Agent" value={metrics?.first_agent} />
            <InfoRow label="Last Agent" value={metrics?.last_agent} />
            <InfoRow label="First Response" value={metrics?.first_response_at ? timeDiff(metrics?.initiated_at, metrics?.first_response_at) : '—'} />
            <InfoRow label="Duration" value={fmtDur(metrics?.duration_seconds)} />
            {metrics?.missed_at && <InfoRow label="Missed At" value={fmtDateTime(metrics.missed_at)} />}
            <InfoRow label="Plan" value={data?.plan_tier ? <span className="capitalize">{data.plan_tier}</span> : '—'} />
            {acc?.account_type && <InfoRow label="Package" value={acc.account_type} />}
          </Collapsible>
        </div>
      )}

      {tab === 'analytics' && (
        <div>
          {analyticsLoading && !analytics ? (
            <div className="flex items-center justify-center py-12 text-txt-tertiary text-xs">Loading analytics…</div>
          ) : (
            <>
              {/* Company header */}
              <div className="flex flex-col items-center text-center px-5 pt-6 pb-5 border-b border-border">
                <p className="text-[11px] font-semibold text-txt-secondary uppercase tracking-wider mb-1">Customer Company</p>
                <p className="text-sm font-semibold text-txt-primary">{analytics?.company_name || displayName}</p>
                {(analytics?.alias || acc?.alias) && <span className="inline-block mt-2 text-[10px] font-medium px-1.5 py-px rounded bg-amber-100 text-amber-700">{analytics?.alias || acc?.alias}</span>}
              </div>

              <Collapsible title="Tech Support Time" defaultOpen>
                <div className="grid grid-cols-3 gap-2 pt-1">
                  <Stat label="Company" value={fmtDur(analytics?.support_seconds_company)} hint={`${analytics?.total_chats ?? 0} chats`} />
                  <Stat label="User" value={fmtDur(analytics?.support_seconds_user)} hint={analytics?.this_user?.name || analytics?.this_user?.email || 'n/a'} />
                  <Stat label="This chat" value={fmtDur(analytics?.support_seconds_chat)} />
                </div>
                {!analytics?.this_user && <p className="text-[10px] text-txt-tertiary mt-2.5 leading-relaxed">Per-user time appears once chats carry the person's identity (email passed by the embed).</p>}
              </Collapsible>

              <Collapsible title="Most Common Issues" defaultOpen>
                {analytics?.top_issues?.length ? (
                  <div className="space-y-2 pt-1">
                    {analytics.top_issues.map((it, i) => (
                      <div key={i} className="flex items-center justify-between gap-2">
                        <span className="text-xs text-txt-primary truncate">{it.issue}</span>
                        <span className="text-[10px] font-semibold px-1.5 py-px rounded bg-slate-100 text-slate-600 shrink-0">{it.count}</span>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-xs text-txt-tertiary pt-1">No issues recorded yet.</p>}
              </Collapsible>

              <Collapsible title="Company Chats" defaultOpen>
                <div className="flex gap-1 p-0.5 rounded-lg bg-surface-secondary mb-3 mt-1">
                  {([['all', 'All chats'], ['unresolved', 'Unresolved']] as const).map(([k, lbl]) => (
                    <button key={k} onClick={() => setChatTab(k)}
                      className={`flex-1 text-[11px] font-medium py-1.5 rounded-md transition-colors ${chatTab === k ? 'bg-surface text-brand-600 shadow-sm' : 'text-txt-secondary hover:text-txt-primary'}`}>
                      {lbl}{k === 'all' ? ` (${analytics?.all_chats?.length ?? 0})` : ` (${analytics?.unresolved_chats?.length ?? 0})`}
                    </button>
                  ))}
                </div>
                {chatList.length ? (
                  <div className="space-y-2">
                    {chatList.map(c => (
                      <button key={c.id} onClick={() => openChat(c.id)}
                        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border border-border hover:bg-surface-hover hover:border-brand-300 transition-colors text-left group">
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-txt-primary truncate">Chat #{c.id}{c.visitor_name ? ` · ${c.visitor_name}` : ''}</p>
                          <p className="text-[10px] text-txt-tertiary mt-0.5">{fmtDateTime(c.created_at)}</p>
                        </div>
                        <span className="flex items-center gap-1.5 shrink-0">
                          <span className={`text-[10px] font-medium px-1.5 py-px rounded capitalize ${c.resolved ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{c.resolved ? 'Resolved' : (c.resolution || c.status || '—')}</span>
                          <svg className="w-3.5 h-3.5 text-txt-tertiary group-hover:text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                        </span>
                      </button>
                    ))}
                  </div>
                ) : <p className="text-xs text-txt-tertiary py-2">{chatTab === 'unresolved' ? 'No unresolved chats. 🎉' : 'No chats yet.'}</p>}
              </Collapsible>
            </>
          )}
        </div>
      )}
      </div>{/* end scrollable body */}
    </div>
  )
}
